import { useEffect, useRef, useState } from 'react'
import { DEVICE_AUTH_INACTIVITY_MINUTES } from '@/common/env'
import { useDeviceAuthActivityTimeout } from './deviceAuth/useActivityTimeout'
import { useIsStandalone } from './webauthn'

type Props = {
    isAuthenticated: boolean
    requestDeviceAuth?: (reason: 'appAuthenticated' | 'inactivityExpired') => Promise<boolean>
    onSuccess?: () => void
    onFailure?: (err?: any) => void
}

export default function DeviceAuthRequester({ isAuthenticated, requestDeviceAuth, onSuccess, onFailure }: Props) {
    const standalone = useIsStandalone()
    const [timeoutEpoch, setTimeoutEpoch] = useState(0)
    const onSuccessRef = useRef(onSuccess)
    const onFailureRef = useRef(onFailure)

    useEffect(() => {
        onSuccessRef.current = onSuccess
    }, [onSuccess])

    useEffect(() => {
        onFailureRef.current = onFailure
    }, [onFailure])

    useDeviceAuthActivityTimeout({
        enabled: isAuthenticated && standalone,
        inactivityMs: +DEVICE_AUTH_INACTIVITY_MINUTES * 60 * 1000,
        onExpired: () => setTimeoutEpoch((value) => value + 1),
    })

    useEffect(() => {
        if (typeof window === 'undefined') return
        if (!isAuthenticated) {
            return
        }
        if (!standalone) {
            return
        }

        let mounted = true

        const run = async () => {
            if (!requestDeviceAuth) {
                return
            }
            try {
                const reason = timeoutEpoch > 0 ? 'inactivityExpired' : 'appAuthenticated'
                const ok = await requestDeviceAuth(reason)
                if (!mounted) return
                if (ok) {
                    if (onSuccessRef.current) {
                        onSuccessRef.current()
                    }
                } else {
                    if (onFailureRef.current) {
                        onFailureRef.current()
                    }
                }
            } catch (err) {
                if (onFailureRef.current) {
                    onFailureRef.current(err)
                }
            }
        }

        void run()
        return () => {
            mounted = false
        }
    }, [isAuthenticated, requestDeviceAuth, standalone, timeoutEpoch])

    return null
}
